import React, { useEffect,useState } from "react";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { FaGithub, FaLinkedin, FaInstagram, FaEnvelope } from "react-icons/fa";

// ✅ Reusable Reveal component for smooth scroll animation
const Reveal = ({ children, delay = 0 }) => {
  const controls = useAnimation();
  const [ref, inView] = useInView({ threshold: 0.2, triggerOnce: true });

  useEffect(() => {
    if (inView) controls.start("visible");
  }, [controls, inView]);

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={{
        hidden: { opacity: 0, y: 30 },
        visible: {
          opacity: 1,
          y: 0,
          transition: { duration: 0.6, delay },
        },
      }}
    >
      {children}
    </motion.div>
  );
};

const Navbar = () => {
  const sections = ["Intro", "Experience", "Projects", "Skills", "Interests", "Contact"];
  const [activeSection, setActiveSection] = useState("Intro");

  useEffect(() => {
  const sectionElements = sections.map((id) =>
    document.getElementById(id.toLowerCase())
  );

  const observer = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        setActiveSection(entry.target.id.charAt(0).toUpperCase() + entry.target.id.slice(1));
      }
    });
  },
  {
    threshold: 0.2, // 20% visible is enough
    rootMargin: "-10% 0px -10% 0px",
  }
);

  const observeSections = () => {
    sectionElements.forEach((el) => el && observer.observe(el));
  };

  if (document.readyState === "complete") {
    observeSections();
  } else {
    window.addEventListener("load", observeSections);
  }

  return () => {
    observer.disconnect();
    window.removeEventListener("load", observeSections);
  };
}, []);

  return (
    <nav className="fixed left-0 top-0 h-screen w-14 md:w-20 bg-gray-900 flex flex-col justify-center items-center z-50 border-r border-gray-700">
      {sections.map((item) => (
        <a
          key={item}
          href={`#${item.toLowerCase()}`}
          className={`flex justify-center items-center py-9 transform -rotate-90 font-semibold text-sm tracking-wider transition ${
            activeSection === item
              ? "text-teal-400 scale-110"
              : "text-gray-400 hover:text-teal-400"
          }`}
          onClick={() => setActiveSection(item)}
        >
          {item}
        </a>
      ))}
    </nav>
  );
};

// ✅ Section Component for structure
const Section = ({ id, title, bg, children }) => (
  <section
    id={id}
    className={`min-h-screen pt-5 pb-5 flex flex-col justify-center px-5 pl-20 md:pl-32 ${bg}`}
  >
    <Reveal>
      <h2 className="text-4xl pt-5 font-bold mb-10 text-teal-400">{title}</h2>
      {children}
    </Reveal>
  </section>
);

// ✅ Intro Section
const Intro = () => (
  <Section id="intro" title="Hey, I'm Ethan Ligi 👋" bg="bg-gray-950 text-white">
    <p className="max-w-2xl text-lg text-gray-300 leading-relaxed">
      I'm a Software Engineer passionate about creating elegant, performant, and user-focused applications.  
      I specialize in modern web technologies like React, Tailwind, and AWS.
    </p>

    <div className="flex space-x-6 mt-10 text-2xl">
      <a href="https://github.com/90090" target="_blank" rel="noreferrer">
        <FaGithub className="hover:text-teal-400 transition" />
      </a>
      <a href="https://linkedin.com/in/eligi" target="_blank" rel="noreferrer">
        <FaLinkedin className="hover:text-teal-400 transition" />
      </a>
      <a href="mailto:ethanligi@gmail.com" target="_blank" rel="noreferrer">
        <FaEnvelope className="hover:text-teal-400 transition" />
      </a>
      <a href="https://instagram.com/ethanligi" target="_blank" rel="noreferrer">
        <FaInstagram className="hover:text-teal-400 transition" />
      </a>
    </div>
  </Section>
);

// ✅ Experience Section
const Experience = () => (
  <Section id="experience" title="Experience" bg="bg-gray-900 text-white">
    <div className="space-y-10 border-l-4 border-teal-500 pl-6">
      <Reveal delay={0.1}>
        <div>
          <h3 className="text-2xl font-semibold">Software Engineer – Social Circle</h3>
          <p className="text-sm text-gray-400 italic">Jan 2024 – Present | Prospect, CT</p>
          <p className="mt-2 text-gray-300">
            Design and develop websites for small businesses using React, Tailwind, and AWS.  
            Manage projects end-to-end and ensure post-launch maintenance.
          </p>
        </div>
      </Reveal>

      <Reveal delay={0.2}>
        <div>
          <h3 className="text-2xl font-semibold">Software Engineering Co-op – Alpine Software</h3>
          <p className="text-sm text-gray-400 italic">May 2023 – Dec 2023 | Mendon, NY</p>
          <p className="mt-2 text-gray-300">
            Enhanced software using Delphi and Python to streamline client workflows by 30%.  
            Worked directly with clients to design custom features and ensure reliability.
          </p>
        </div>
      </Reveal>
    </div>
  </Section>
);

// ✅ Projects Section
const Projects = () => (
  <Section id="projects" title="Projects" bg="bg-gray-950 text-white">
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
      {[
        {
          title: "Emerald Screen Printing",
          desc: "Full-stack AWS + React web app for a local business.",
          img: "../emerald.png",
          site: "https://emeraldscreenprinting.com/",
          git: "https://github.com/90090/emeraldscreenprinting",
        },
        {
          title: "Bees Vending",
          desc: "Full-stack AWS + React web app for a vending machine supply company.",
          img: "../BeesVending.png",
          site: "https://beesvending.com/",
          git: "https://github.com/90090/bees-vending",
        },
        {
          title: "Jimmy's of Watertown",
          desc: "E-commerce site for a local restaurant built with React and Tailwind.",
          img: "../Jimmys.png",
          site: "https://jimmysofwatertown.com/",
          git: "https://github.com/90090/jimmy",
        },
        {
          title: "Naomi Portfolio",
          desc: "Model portfolio site built with React and Tailwind.",
          img: "../Naomi.png",
          site: "https://naomiokolo.com/",
          git: "https://github.com/90090/naomi-portfolio",
        },
        {
          title: "Dandy Garden",
          desc: "Chrome extension built at RIT Hackathon for productivity.",
          img: "../DandyGarden.png",
          git: "https://github.com/90090/DandyGarden",
        },
      ].map((p, i) => (
        <Reveal key={i} delay={i * 0.15}>
          <motion.div
            whileHover={{ scale: 1.05 }}
            className="bg-gray-800 rounded-lg overflow-hidden shadow-lg transition-transform w-full"
          >
            <img src={p.img} alt={p.title} className="w-full h-40 object-cover" />
            <div className="p-4">
              <h3 className="text-md md:text-xl font-bold text-teal-400">{p.title}</h3>
              <p className="text-gray-300 mt-2">{p.desc}</p>
              <div className="flex space-x-4 mt-4">
                {p.site && (
                  <a href={p.site} target="_blank" rel="noreferrer" className="text-sm text-teal-400 hover:underline">
                    Site
                  </a>
                )}
                {p.git && (
                  <a href={p.git} target="_blank" rel="noreferrer" className="text-sm text-teal-400 hover:underline">
                    GitHub
                  </a>
                )}
              </div>
            </div>
          </motion.div>
        </Reveal>
      ))}
    </div>
  </Section>
);

// ✅ Skills Section
const Skills = () => (
  <Section id="skills" title="Skills" bg="bg-gray-900 text-white">
    <div className="flex flex-wrap justify-center gap-2 sm:gap-4 max-w-5xl mx-auto">
      {[
        "JavaScript", "React", "AWS", "Gitflow", "Chrome API", "Tailwind", "Python",
        "Java", "C", "Delphi", "HTML", "GitHub", "RESTful API", "CSS", "PostgreSQL",
      ].map((skill, i) => (
        <Reveal key={i} delay={i * 0.05}>
          <motion.span
            whileHover={{ scale: 1.1 }}
            className="
              bg-gray-800 
              text-sm md:text-base 
              px-2 sm:px-3 md:px-4 
              py-1 sm:py-1.5 md:py-2 
              rounded-lg shadow-md 
              whitespace-nowrap
            "
          >
            {skill}
          </motion.span>
        </Reveal>
      ))}
    </div>
  </Section>
);

// ✅ Interests Section
const Interests = () => (
  <Section id="interests" title="Interests" bg="bg-gray-950 text-white">
    <div className="
      grid 
      grid-cols-1 sm:grid-cols-3 md:grid-cols-4 
      gap-2 sm:gap-4 md:gap-6 
      max-w-5xl mx-auto
    ">
      {[
        "Hiking", "Photography", "Climbing", "Freelance Web Design",
        "Fitness", "Hockey", "Emerging Tech",
      ].map((interest, i) => (
        <Reveal key={i} delay={i * 0.1}>
          <motion.div
            whileHover={{ scale: 1.05 }}
            className="
              p-3 sm:p-4 md:p-6 
              text-center 
              text-sm md:text-base 
              bg-gray-800 rounded-lg shadow-lg 
              hover:bg-gray-700 transition
            "
          >
            {interest}
          </motion.div>
        </Reveal>
      ))}
    </div>
  </Section>
);


// ✅ Contact Section
const Contact = () => (
  <Section id="contact" title="Contact Me" bg="bg-gray-900 text-white">
    <p className="text-gray-300 mb-6">
      I'm open to new opportunities! Feel free to reach out or connect with me.
    </p>
    <div className="space-x-6 text-3xl">
      <a href="mailto:ethanligi@gmail.com"><FaEnvelope className="inline hover:text-teal-400" /></a>
      <a href="https://linkedin.com/in/eligi" target="_blank" rel="noreferrer"><FaLinkedin className="inline hover:text-teal-400" /></a>
      <a href="https://github.com/90090" target="_blank" rel="noreferrer"><FaGithub className="inline hover:text-teal-400" /></a>
    </div>
  </Section>
);

// ✅ Main App Component
export default function App() {
  return (
    <div className="font-sans bg-gray-950 min-h-screen">
      <Navbar />
      <Intro />
      <Experience />
      <Projects />
      <Skills />
      <Interests />
      <Contact />
    </div>
  );
}
